import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useBooking } from '../context/BookingContext';
import { useAPICallTracker } from './useDebugTracker';

interface Extra {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image_url?: string | null;
  highlight_text?: string | null;
  max_quantity?: number | null;
  pricing_type?: string;
}

interface UseRealtimeExtrasReturn {
  extras: Extra[];
  isLoading: boolean;
  error: string | null;
}

export const useRealtimeExtras = (): UseRealtimeExtrasReturn => {
  const { selectedHotel } = useBooking();
  const [extras, setExtras] = useState<Extra[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { trackCall } = useAPICallTracker('useRealtimeExtras');

  const fetchExtras = useCallback(async () => {
    if (!selectedHotel) {
      console.warn('⚠️ No selected hotel, cannot fetch extras');
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      
      // Use selectedHotel.id directly
      const hotelId = selectedHotel.id;
      trackCall(`/extras?hotel_id=${hotelId}`, 'GET', { hotelId });

      // Then fetch extras for this hotel
      const { data: extrasData, error: extrasError } = await supabase
        .from('extras')
        .select('*')
        .eq('hotel_id', hotelId)
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (extrasError) throw extrasError;

      console.log('📦 Fetched extras:', extrasData?.length || 0);
      setExtras(extrasData || []);
    } catch (err) {
      console.error('❌ Error fetching extras:', err);
      setError(err instanceof Error ? err.message : 'Failed to load extras');
    } finally {
      setIsLoading(false);
    }
  }, [selectedHotel?.id, trackCall]); // Only depend on hotel ID, not the entire object

  useEffect(() => {
    if (!selectedHotel?.id) {
      setIsLoading(false);
      return;
    }

    fetchExtras();

    // Set up real-time subscription
    console.log('🔗 Setting up real-time subscription for extras');
    
    const channel = supabase
      .channel(`extras-changes-${selectedHotel.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'extras',
          filter: `hotel_id=eq.${selectedHotel.id}`
        },
        (payload) => {
          console.log('🔄 Real-time extras update:', payload.eventType);
          // Refetch data when any extras change for this hotel
          fetchExtras();
        }
      )
      .subscribe((status) => {
        console.log('📡 Extras subscription status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up extras real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [selectedHotel?.id, fetchExtras]);

  return { extras, isLoading, error };
};
